import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate } from "@/pages/admin/util";
import { Loader2, RefreshCw, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";

export default function SeoLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/seo-leads");
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-seo-leads-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow">SEO leads</p>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Inbound from Landing Pages</h1>
          <p className="text-sm text-[#475569] mt-1">Captured by inline forms on the SEO landing pages.</p>
        </div>
        <button onClick={load} className="btn-outline h-10 py-0 px-4 text-sm" data-testid="seo-leads-refresh">
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      <div className="bg-white border border-[#D7E7DF] overflow-hidden rounded-2xl">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4ED] text-[#475569] text-left">
            <tr>
              <th className="px-4 py-3">Captured</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Page</th>
              <th className="px-4 py-3">File</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2DCD0]">
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={16}/></td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#475569]">
                No SEO leads yet. Once your landing pages start ranking, captured leads will appear here.
              </td></tr>
            )}
            {!loading && items.map((l) => (
              <tr key={l.id} className="hover:bg-[#F7F4ED]/40" data-testid={`seo-lead-row-${l.id}`}>
                <td className="px-4 py-3 text-[#475569] whitespace-nowrap">{fmtDate(l.created_at, true)}</td>
                <td className="px-4 py-3 font-medium text-[#071A33]">{l.name}</td>
                <td className="px-4 py-3"><a href={`mailto:${l.email}`} className="text-[#064E2E] hover:underline">{l.email}</a></td>
                <td className="px-4 py-3 text-[#475569] text-xs">{l.page_url || "—"}</td>
                <td className="px-4 py-3">
                  {l.upload_id
                    ? <a href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${l.upload_id}`} target="_blank" rel="noreferrer" className="text-[#0F766E] inline-flex items-center gap-1 text-xs hover:underline">
                        <FileText size={12}/> {l.upload_filename || "file"} <ExternalLink size={10}/>
                      </a>
                    : <span className="text-[#94A3B8] text-xs">—</span>}
                </td>
                <td className="px-4 py-3">
                  <span className="text-[10px] uppercase tracking-[0.1em] bg-[#B88A2A]/15 text-[#B88A2A] px-1.5 py-0.5 font-medium">
                    {l.status || "new"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
